import moment from "moment";
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { Card } from "reactstrap";

import { ReactComponent as BackIcon } from "assets/img/svg/BackArrow.svg";
import AAutoComplete from "views/pages/manage/common/form-fields-mui/AAutoComplete";
import AButton from "views/pages/manage/common/form-fields-mui/AButton";

import {
  fetchStateAndYearDropdowns,
  createPaymentSlab,
  paymentSlabStatusOptions,
  fetchItemTypeOptions,
} from "../helper";

const CreateNewSlab = () => {
  const history = useHistory();
  const masterDropdowns = useSelector(
    (state) => state.globalData.masterDropdowns,
  );
  const buOptions = (masterDropdowns?.businessArea || []).map((item) => ({
    label: item.label,
    value: item.businessAreaId,
  }));

  const [dropdownOptions, setDropdownOptions] = useState({
    stateOptions: [],
    yearOptions: [],
  });

  const [itemTypeOptions, setItemTypeOptions] = useState([]);

  const [form, setForm] = useState({
    state: null,
    bu: null,
    year: moment().format("YYYY"),
    itemTypeFrom: null,
    itemTypeTo: null,
    status: { label: "Active", value: "ACTIVE" },
  });

  const [effectiveDate, setEffectiveDate] = useState(
    moment().format("YYYY-MM-DD"),
  );

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Fetch dropdowns
    fetchStateAndYearDropdowns().then((options) => {
      // Filter out years smaller than current year
      const currentYear = parseInt(moment().format("YYYY"), 10);
      const filteredYearOptions = (options.yearOptions || []).filter((opt) => {
        // Try to parse as int, fallback to label if value is not a number
        const yearVal = parseInt(opt.label, 10);
        return !isNaN(yearVal) && yearVal >= currentYear;
      });
      setDropdownOptions({
        ...options,
        yearOptions: filteredYearOptions,
      });
    });
    // Fetch item type categories for Item Type From/To using helper function
    fetchItemTypeOptions()
      .then(setItemTypeOptions)
      .catch((err) => {
        console.error("Failed to fetch item type categories", err);
      });
  }, []);

  const handleFormChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // Validation: all required fields must be filled
  const isFormValid =
    form.state &&
    form.bu &&
    form.year &&
    form.itemTypeFrom &&
    form.itemTypeTo &&
    form.status;

  const handleSaveChanges = async () => {
    if (!isFormValid) return;

    setSaving(true);
    try {
      const payload = {
        state: form.state?.label,
        stateId: form.state?.value,
        businessUnitId: form.bu?.value,
        businessUnit: form.bu?.label,
        year: form.year.label,
        yearId: form.year.value,

        itemTypeFrom: form.itemTypeFrom?.key,
        itemTypeFromId: form.itemTypeFrom?.value,
        itemTypeTo: form.itemTypeTo?.key,
        itemTypeToId: form.itemTypeTo?.value,

        effectiveDate: effectiveDate,
        status: form.status?.value,
      };

      const response = await createPaymentSlab(payload);
      if (response?.id) {
        // Redirect to ViewEditSlab with the new slab ID
        history.push(`/admin/paymentSlab/${response.id}`);
      }
    } catch (error) {
      console.error("Error creating slab:", error);
      alert("Error creating slab. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4">
      <div className="d-flex align-items-center mb-3">
        <BackIcon
          style={{ cursor: "pointer" }}
          onClick={() => history.goBack()}
        />
        <h3 className="heading-3 mb-0 ml-2">Create New Slab</h3>
      </div>

      <Card className="p-4 mb-4">
        <h3 className="mb-4 text-bold">Slab setup</h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "20px",
            marginBottom: "20px",
          }}
        >
          {/* Effective Date */}
          <div>
            <label
              style={{ display: "block", marginBottom: "8px", fontWeight: 500 }}
            >
              Effective date
            </label>
            <input
              type="date"
              value={effectiveDate}
              disabled={true}
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                backgroundColor: "#f5f5f5",
                cursor: "not-allowed",
              }}
            />
          </div>

          {/* State */}
          <div>
            <AAutoComplete
              label="State"
              placeholder="Select"
              isMulti={false}
              items={dropdownOptions.stateOptions}
              currentValue={form.state}
              handleChange={(value) => handleFormChange("state", value)}
              height="45px"
            />
          </div>

          {/* Business Unit */}
          <div>
            <AAutoComplete
              label="Business Unit"
              placeholder="Select"
              isMulti={false}
              items={buOptions}
              currentValue={form.bu}
              handleChange={(value) => handleFormChange("bu", value)}
              height="45px"
            />
          </div>

          {/* Year (academic year dropdown, no past years) */}
          <div>
            <AAutoComplete
              label="Year"
              placeholder="Select"
              isMulti={false}
              items={dropdownOptions.yearOptions}
              currentValue={form.year}
              handleChange={(value) => handleFormChange("year", value)}
              height="45px"
            />
          </div>

          {/* Item Type From */}
          <div>
            <AAutoComplete
              label="Item Type From"
              placeholder="Select"
              isMulti={false}
              items={itemTypeOptions}
              currentValue={form.itemTypeFrom}
              handleChange={(value) => handleFormChange("itemTypeFrom", value)}
              height="45px"
            />
          </div>

          {/* Item Type To */}
          <div>
            <AAutoComplete
              label="Item Type To"
              placeholder="Select"
              isMulti={false}
              items={itemTypeOptions}
              currentValue={form.itemTypeTo}
              handleChange={(value) => handleFormChange("itemTypeTo", value)}
              height="45px"
            />
          </div>

          {/* Status */}
          <div>
            <AAutoComplete
              label="Status"
              placeholder="Select"
              isMulti={false}
              items={paymentSlabStatusOptions}
              currentValue={form.status}
              handleChange={(value) => handleFormChange("status", value)}
              height="45px"
            />
          </div>
        </div>

        <div style={{ width: "200px" }}>
          <AButton
            variant="primary_filled"
            onClick={handleSaveChanges}
            disabled={!isFormValid || saving}
            className="w-full m-auto"
          >
            {saving ? "Saving..." : "Save changes"}
          </AButton>
        </div>
      </Card>
    </div>
  );
};

export default CreateNewSlab;
